/**
 * Booking Rules
 *
 * Lightweight per-listing scheduling rules that a provider can configure
 * on the availability tab. The rules are stored in
 * `listing.attributes.publicData.bookingRules` so they are readable by
 * both the marketplace API clients and backend event handlers.
 *
 * Rule shape:
 *   {
 *     autoAccept: boolean,              // auto-accept booking requests after payment
 *     minimumNoticeMinutes: number,     // earliest allowed booking relative to now
 *     bookingWindowDays: number,        // how far into the future bookings can be made
 *     bufferMinutes: number             // minimum gap between consecutive bookings
 *   }
 */

export const BOOKING_RULES_PUBLIC_DATA_KEY = 'bookingRules';

// Operator-only metadata key on the listing that the server writes to keep
// track of buffer-generated availability exceptions. The UI uses this to
// visually distinguish (and lock) exceptions created by the buffer rule.
export const BUFFER_EXCEPTIONS_LISTING_METADATA_KEY = 'bookingRulesBuffer';

/**
 * Extract the set of availability exception ids that belong to buffer
 * rule. Returns a Set<string>. Pass either a listing entity or its
 * attributes/metadata.
 */
export const getBufferExceptionIds = listing => {
  const metadata =
    listing?.attributes?.metadata || listing?.metadata || listing || {};
  const map = metadata?.[BUFFER_EXCEPTIONS_LISTING_METADATA_KEY];
  if (!map || typeof map !== 'object') return new Set();
  return new Set(Object.keys(map));
};

// Hard ceilings so we never ask the API for bookings outside what it supports.
// Sharetribe only generates time slots up to 90 days ahead, so asking for a
// larger window would be pointless on the marketplace side.
export const MAX_BOOKING_WINDOW_DAYS = 90;
export const MIN_BOOKING_WINDOW_DAYS = 1;

export const MAX_MINIMUM_NOTICE_MINUTES = 60 * 24 * 7; // 7 days
export const MIN_MINIMUM_NOTICE_MINUTES = 0;

export const MAX_BUFFER_MINUTES = 120; // 2 hours
export const MIN_BUFFER_MINUTES = 0;

/**
 * Initial values shown to a provider the first time they open the rules form.
 * Unconfigured listings still behave as before (see `NO_RESTRICTION_RULES`);
 * these are only applied once the provider actively saves the form.
 */
export const DEFAULT_BOOKING_RULES = Object.freeze({
  autoAccept: false,
  minimumNoticeMinutes: 0,
  bookingWindowDays: MAX_BOOKING_WINDOW_DAYS,
  bufferMinutes: 0,
});

/**
 * Effective rules when a listing hasn't configured any. Picks values that
 * preserve the existing marketplace behaviour (no minimum notice, no
 * buffer, booking window equal to the marketplace-wide maximum, manual
 * accept).
 */
export const NO_RESTRICTION_RULES = Object.freeze({
  autoAccept: false,
  minimumNoticeMinutes: 0,
  bookingWindowDays: MAX_BOOKING_WINDOW_DAYS,
  bufferMinutes: 0,
});

const toFiniteNumber = (value, fallback) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Normalize raw public data into a complete, safe booking-rules object.
 * Missing or invalid fields fall back to the provided `fallback` shape
 * (which defaults to `NO_RESTRICTION_RULES`). Pass `DEFAULT_BOOKING_RULES`
 * explicitly when you want the opinionated form defaults.
 */
export const normalizeBookingRules = (raw = {}, fallback = NO_RESTRICTION_RULES) => {
  const minimumNoticeMinutes = clamp(
    toFiniteNumber(raw.minimumNoticeMinutes, fallback.minimumNoticeMinutes),
    MIN_MINIMUM_NOTICE_MINUTES,
    MAX_MINIMUM_NOTICE_MINUTES
  );
  const bookingWindowDays = clamp(
    toFiniteNumber(raw.bookingWindowDays, fallback.bookingWindowDays),
    MIN_BOOKING_WINDOW_DAYS,
    MAX_BOOKING_WINDOW_DAYS
  );
  const bufferMinutes = clamp(
    toFiniteNumber(raw.bufferMinutes, fallback.bufferMinutes),
    MIN_BUFFER_MINUTES,
    MAX_BUFFER_MINUTES
  );

  return {
    autoAccept: !!raw.autoAccept,
    minimumNoticeMinutes: Math.round(minimumNoticeMinutes),
    bookingWindowDays: Math.round(bookingWindowDays),
    bufferMinutes: Math.round(bufferMinutes),
  };
};

/**
 * True when the listing has the booking-rules public-data key set (even
 * if empty) – i.e. the provider has opened the form at least once.
 */
export const hasBookingRulesConfigured = listing => {
  const publicData = listing?.attributes?.publicData || listing?.publicData || listing || {};
  const raw = publicData?.[BOOKING_RULES_PUBLIC_DATA_KEY];
  return raw != null && typeof raw === 'object';
};

/**
 * Read booking rules from a listing (either a Flex listing entity or a
 * plain attributes/publicData-ish object). Listings without any rules
 * configured fall back to `NO_RESTRICTION_RULES` so existing behaviour
 * is preserved.
 */
export const getBookingRulesFromListing = listing => {
  const publicData =
    listing?.attributes?.publicData ||
    listing?.publicData ||
    listing ||
    {};
  const raw = publicData?.[BOOKING_RULES_PUBLIC_DATA_KEY];
  return normalizeBookingRules(raw || {}, NO_RESTRICTION_RULES);
};

/**
 * Clamp the app-level `dayCountAvailableForBooking` to the listing's
 * configured booking window.
 */
export const effectiveDayCountAvailableForBooking = (
  dayCountAvailableForBooking,
  bookingRules
) => {
  const rules = bookingRules ? normalizeBookingRules(bookingRules) : DEFAULT_BOOKING_RULES;
  const base = Number.isFinite(dayCountAvailableForBooking)
    ? dayCountAvailableForBooking
    : MAX_BOOKING_WINDOW_DAYS;
  return Math.max(1, Math.min(base, rules.bookingWindowDays));
};

/**
 * Return the minimum (inclusive) Date a booking is allowed to start at,
 * given the minimum-notice rule. If no notice is configured, returns
 * the provided reference date.
 */
export const minimumBookingStartDate = (bookingRules, referenceDate = new Date()) => {
  const rules = bookingRules ? normalizeBookingRules(bookingRules) : DEFAULT_BOOKING_RULES;
  if (!rules.minimumNoticeMinutes) {
    return referenceDate;
  }
  return new Date(referenceDate.getTime() + rules.minimumNoticeMinutes * 60 * 1000);
};

/**
 * Does this start time honor the minimum-notice rule?
 * @param {Date|number} startTime Date or epoch ms
 */
export const isStartTimeWithinNoticeWindow = (
  startTime,
  bookingRules,
  referenceDate = new Date()
) => {
  if (!startTime) {
    return false;
  }
  const rules = bookingRules ? normalizeBookingRules(bookingRules) : DEFAULT_BOOKING_RULES;
  if (!rules.minimumNoticeMinutes) {
    return false;
  }
  const startMs = startTime instanceof Date ? startTime.getTime() : Number(startTime);
  if (!Number.isFinite(startMs)) {
    return false;
  }
  const earliestAllowed = referenceDate.getTime() + rules.minimumNoticeMinutes * 60 * 1000;
  return startMs < earliestAllowed;
};
